/**
 * Bayilik landing görsel mockup'ları — saf CSS/SVG, gerçek ekran görüntüsü
 * gerektirmez. İç yüzeyler sabit koyu (ürün arayüzü hissi) olduğu için sayfa
 * ister açık ister koyu temada olsun pop eder. Animasyonlar index.css'te
 * (bayilik-*) + framer-motion.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Search, Home, ReceiptText, User, Bell, Star, Clock, Bike, Zap,
  LayoutGrid, Map as MapIcon, Wallet, Settings, TrendingUp,
  Power, Navigation, Package, Flame, Coins, CheckCircle2, FileCheck2,
} from 'lucide-react';

/* -------- Animasyonlu sayaç -------- */
export function Counter({ to = 0, suffix = '', duration = 1600, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref} className={className}>{val.toLocaleString('tr-TR')}{suffix}</span>;
}

/* -------- Müşteri uygulaması ana sayfası (telefon) -------- */
export function PhoneApp() {
  const cats = ['Yemek', 'Market', 'Su', 'Tatlı', 'Çiçek'];
  const rests = [
    { ad: 'Lezzet Durağı', puan: '4.8', sure: '20-30 dk', g: 'from-orange-500/70 to-rose-600/70' },
    { ad: 'Burger Keyfi', puan: '4.7', sure: '15-25 dk', g: 'from-amber-400/70 to-orange-600/70' },
  ];
  return (
    <div className="animate-float w-[250px] shrink-0 rounded-[2.4rem] bg-slate-900 p-2.5 shadow-2xl ring-1 ring-white/10">
      <div className="relative h-[520px] overflow-hidden rounded-[1.95rem] bg-[#0b1020] text-white">
        {/* notch */}
        <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-slate-900" />
        {/* status */}
        <div className="flex items-center justify-between px-5 pt-3 text-[10px] text-white/60">
          <span>20:41</span><span>Agros Kapında</span>
        </div>
        {/* header */}
        <div className="flex items-center justify-between px-4 pt-3">
          <div>
            <p className="text-[9px] text-white/50">Teslimat adresi</p>
            <p className="text-[12px] font-semibold">Merkez, Isparta ▾</p>
          </div>
          <div className="relative rounded-full bg-white/10 p-1.5">
            <Bell className="h-4 w-4" />
            <span className="absolute -right-0 -top-0 h-2 w-2 rounded-full bg-orange-500" />
          </div>
        </div>
        {/* search */}
        <div className="mx-4 mt-3 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2">
          <Search className="h-3.5 w-3.5 text-white/60" />
          <span className="text-[11px] text-white/50">Restoran ya da ürün ara</span>
        </div>
        {/* categories */}
        <div className="mt-3 flex gap-2 overflow-hidden px-4">
          {cats.map((c, i) => (
            <span key={c} className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] ${i === 0 ? 'bg-orange-500 text-white' : 'bg-white/8 text-white/70'}`}>{c}</span>
          ))}
        </div>
        {/* promo */}
        <div className="relative mx-4 mt-3 overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 p-3 bayilik-sheen">
          <p className="text-[12px] font-bold">İlk siparişe %20</p>
          <p className="text-[9px] text-white/80">Agros Kapında’ya özel</p>
        </div>
        {/* restaurants */}
        <div className="mt-3 space-y-2 px-4">
          {rests.map((r) => (
            <div key={r.ad} className="flex gap-2.5 rounded-xl bg-white/5 p-2">
              <div className={`h-12 w-12 shrink-0 rounded-lg bg-gradient-to-br ${r.g}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-semibold">{r.ad}</p>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-white/60">
                  <span className="flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{r.puan}</span>
                  <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{r.sure}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* bottom nav */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-white/10 bg-[#0b1020]/95 px-2 py-2.5 text-[9px]">
          {[['Anasayfa', Home, true], ['Siparişler', ReceiptText], ['Ara', Search], ['Profil', User]].map(([l, I, a]) => {
            const Icon = I;
            return (
              <div key={l} className={`flex flex-col items-center gap-0.5 ${a ? 'text-orange-500' : 'text-white/50'}`}>
                <Icon className="h-4 w-4" />{l}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------- Kurye uygulaması ana sayfası (telefon) -------- */
export function CourierApp() {
  const sira = [
    { ad: 'Lezzet Durağı', ilce: 'Merkez', km: '1.8 km', ucret: '₺58' },
    { ad: 'Burger Keyfi', ilce: 'Çünür', km: '3.2 km', ucret: '₺72' },
  ];
  return (
    <div className="animate-float w-[250px] shrink-0 rounded-[2.4rem] bg-slate-900 p-2.5 shadow-2xl ring-1 ring-white/10">
      <div className="relative h-[520px] overflow-hidden rounded-[1.95rem] bg-[#0b1020] text-white">
        <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-slate-900" />
        <div className="flex items-center justify-between px-5 pt-3 text-[10px] text-white/60">
          <span>20:41</span><span>AgrosJet Kurye</span>
        </div>
        {/* header */}
        <div className="flex items-center justify-between px-4 pt-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-rose-600 text-[11px] font-bold">AK</div>
            <div>
              <p className="text-[9px] text-white/50">Merhaba</p>
              <p className="text-[12px] font-semibold">Ahmet K.</p>
            </div>
          </div>
          <span className="flex items-center gap-1 rounded-full bg-green-500/20 px-2 py-1 text-[9px] font-medium text-green-400">
            <Power className="h-3 w-3" /> Çevrimiçi
          </span>
        </div>
        {/* rank + shift */}
        <div className="mx-4 mt-3 flex items-center justify-between rounded-lg bg-white/5 px-3 py-1.5 text-[9px]">
          <span className="flex items-center gap-1 text-amber-400"><Star className="h-3 w-3 fill-amber-400" /> Rütbe I</span>
          <span className="text-white/60">Vardiya 18:00–00:00 · <span className="text-green-400">✓ Check-in</span></span>
        </div>
        {/* earnings */}
        <div className="relative mx-4 mt-3 overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 p-3 bayilik-sheen">
          <p className="text-[9px] text-white/80">Bugünkü kazanç</p>
          <p className="text-[20px] font-extrabold leading-tight">₺1.240</p>
          <div className="mt-1 flex items-end gap-0.5">
            {[5, 8, 6, 10, 7, 12, 9].map((h, i) => (
              <span key={i} className="w-2 rounded-sm bg-white/70" style={{ height: h * 2 }} />
            ))}
            <span className="ml-auto text-[9px] text-white/90">12 teslimat</span>
          </div>
        </div>
        {/* active delivery */}
        <div className="mx-4 mt-3 rounded-xl border border-orange-500/30 bg-white/5 p-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-orange-400">Aktif Teslimat · #2043</span>
            <span className="flex items-center gap-1 text-[9px] text-white/60"><Clock className="h-3 w-3" /> 8 dk</span>
          </div>
          <div className="mt-2 space-y-1 text-[10px]">
            <p className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-violet-400" /> Lezzet Durağı</p>
            <p className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-orange-400" /> Merkez, 3. Cadde No:12</p>
          </div>
          <button className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-orange-500 py-1.5 text-[11px] font-semibold">
            <Navigation className="h-3 w-3" /> Teslim Et
          </button>
        </div>
        {/* queue */}
        <p className="mx-4 mt-3 text-[9px] uppercase tracking-wider text-white/40">Sıradaki</p>
        <div className="mx-4 mt-1 space-y-1.5">
          {sira.map((s) => (
            <div key={s.ad} className="flex items-center gap-2 rounded-lg bg-white/5 px-2.5 py-1.5">
              <Package className="h-3.5 w-3.5 text-white/50" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] font-medium">{s.ad}</p>
                <p className="text-[9px] text-white/50">{s.ilce} · {s.km}</p>
              </div>
              <span className="text-[11px] font-semibold text-green-400">{s.ucret}</span>
            </div>
          ))}
        </div>
        {/* bottom nav */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-white/10 bg-[#0b1020]/95 px-2 py-2.5 text-[9px]">
          {[['Teslimat', Bike, true], ['Harita', MapIcon], ['Kazanç', Wallet], ['Profil', User]].map(([l, I, a]) => {
            const Icon = I;
            return <div key={l} className={`flex flex-col items-center gap-0.5 ${a ? 'text-orange-500' : 'text-white/50'}`}><Icon className="h-4 w-4" />{l}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

/* -------- Mini panel görselleri (özellik bölümleri için) -------- */
function MiniCard({ children }) {
  return <div className="w-full overflow-hidden rounded-2xl bg-[#0b1020] p-4 text-white shadow-2xl ring-1 ring-white/10">{children}</div>;
}

export function MiniJeton() {
  const rows = [
    { t: 'Sipariş #2043', v: '−2', p: false }, { t: 'Sipariş #2044', v: '−2', p: false },
    { t: 'Jeton yükleme', v: '+500', p: true }, { t: 'Sipariş #2042', v: '−2', p: false },
  ];
  return (
    <MiniCard>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Coins className="h-5 w-5 text-amber-400" /><span className="text-sm font-semibold">Jeton Bakiyesi</span></div>
        <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-xs font-bold text-amber-400">Otomatik düşüm</span>
      </div>
      <p className="mt-3 text-3xl font-extrabold">3.248 <span className="text-base font-medium text-white/50">jeton</span></p>
      <div className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-1.5 text-xs">
            <span className="text-white/70">{r.t}</span>
            <span className={r.p ? 'font-semibold text-green-400' : 'font-semibold text-white/80'}>{r.v}</span>
          </div>
        ))}
      </div>
    </MiniCard>
  );
}

export function MiniVardiya() {
  const gunler = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  const dolu = [1, 1, 0, 1, 1, 1, 0];
  return (
    <MiniCard>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-violet-400" /><span className="text-sm font-semibold">Haftalık Vardiya</span></div>
        <span className="flex items-center gap-1 rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-bold text-orange-400"><Flame className="h-3 w-3" /> Kademeli</span>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {gunler.map((g, i) => (
          <div key={g} className={`flex flex-col items-center gap-1 rounded-lg py-2 text-[10px] ${dolu[i] ? 'bg-violet-600/30 text-violet-200' : 'bg-white/5 text-white/40'}`}>
            {g}<span className={`h-1.5 w-1.5 rounded-full ${dolu[i] ? 'bg-violet-400' : 'bg-white/20'}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <span className="rounded-full bg-amber-400/15 px-2 py-0.5 font-semibold text-amber-400">Rütbe I</span>
        <span className="rounded-full bg-white/8 px-2 py-0.5 text-white/60">Rütbe II</span>
        <span className="ml-auto text-white/50">Çalışma Puanı 92</span>
      </div>
    </MiniCard>
  );
}

export function MiniMutabakat() {
  const rows = [['Nakit', '₺420'], ['Kart', '₺380'], ['POS', '₺1.100']];
  return (
    <MiniCard>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><ReceiptText className="h-5 w-5 text-green-400" /><span className="text-sm font-semibold">Gün Sonu Mutabakat</span></div>
        <span className="flex items-center gap-1 rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-bold text-green-400"><CheckCircle2 className="h-3 w-3" /> Mutabık</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {rows.map(([l, v]) => (
          <div key={l} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-1.5 text-xs">
            <span className="text-white/70">{l}</span>
            <span className="flex items-center gap-1 font-semibold"><CheckCircle2 className="h-3 w-3 text-green-400" />{v}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-xs">
        <FileCheck2 className="h-4 w-4 text-green-400" /> e-Fatura otomatik kesildi · eLogo
      </div>
    </MiniCard>
  );
}

/* -------- Yönetici paneli — canlı harita + otomatik atama (mockup) -------- */
export function Dashboard() {
  return (
    <div className="w-full overflow-hidden rounded-2xl bg-slate-900 shadow-2xl ring-1 ring-white/10">
      {/* browser bar */}
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
        <div className="ml-3 flex-1 rounded-md bg-white/8 px-3 py-1 text-[10px] text-white/50">panel.agrosjet.app</div>
      </div>

      <div className="flex">
        {/* sidebar */}
        <div className="hidden w-12 flex-col items-center gap-4 border-r border-white/10 py-4 text-white/40 sm:flex">
          {[LayoutGrid, MapIcon, Bike, Wallet, TrendingUp, Settings].map((I, i) => (
            <I key={i} className={`h-4 w-4 ${i === 1 ? 'text-orange-500' : ''}`} />
          ))}
        </div>

        {/* main */}
        <div className="relative flex-1 bg-[#0b1020]">
          {/* stats row */}
          <div className="flex flex-wrap gap-2 p-3">
            {[['Aktif kurye', '24'], ['Bekleyen', '3'], ['Ort. süre', '12 dk'], ['Bugün', '418']].map(([l, v]) => (
              <div key={l} className="flex-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5">
                <p className="text-[9px] text-white/50">{l}</p>
                <p className="text-[13px] font-bold text-white">{v}</p>
              </div>
            ))}
          </div>

          {/* map */}
          <div className="relative mx-3 mb-3 h-[240px] overflow-hidden rounded-xl border border-white/10 neon-grid-bg bg-[#0a0e1c]">
            <svg viewBox="0 0 600 300" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice">
              {/* roads */}
              <g stroke="rgba(255,255,255,0.07)" strokeWidth="10" fill="none">
                <path d="M-20 90 H620" /><path d="M-20 210 H620" />
                <path d="M160 -20 V320" /><path d="M430 -20 V320" />
              </g>
              {/* AI route */}
              <path className="bayilik-route" d="M120 230 C 200 150, 300 200, 360 120 S 470 70, 500 80"
                fill="none" stroke="#FF6A00" strokeWidth="4" strokeLinecap="round"
                style={{ filter: 'drop-shadow(0 0 6px rgba(255,106,0,.7))' }} />
            </svg>

            {/* restaurant pin */}
            <div className="absolute" style={{ left: '18%', top: '74%' }}>
              <div className="rounded-md bg-violet-600 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-lg">Restoran</div>
            </div>
            {/* home pin */}
            <div className="absolute" style={{ left: '80%', top: '24%' }}>
              <div className="rounded-md bg-orange-500 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-lg">Müşteri</div>
            </div>
            {/* courier dots with ping */}
            {[['42%', '46%'], ['62%', '66%'], ['30%', '30%']].map(([l, t], i) => (
              <div key={i} className="absolute" style={{ left: l, top: t }}>
                <span className="bayilik-ping absolute -inset-1 rounded-full bg-cyan-400/40" />
                <span className="relative block h-2.5 w-2.5 rounded-full bg-cyan-400 ring-2 ring-[#0a0e1c]" />
              </div>
            ))}

            {/* auto-assign toast */}
            <div className="bayilik-toast absolute left-3 top-3 flex items-center gap-2 rounded-lg border border-orange-500/40 bg-slate-900/90 px-3 py-2 shadow-xl backdrop-blur">
              <Zap className="h-4 w-4 text-orange-500" />
              <div className="text-[10px] leading-tight text-white">
                <p className="font-semibold">Otomatik atama</p>
                <p className="text-white/60">Kurye #15 → Sipariş #2043</p>
              </div>
            </div>

            {/* optimization badge */}
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-600/20 px-2.5 py-1 text-[9px] font-medium text-violet-200">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" /> Akıllı Optimizasyon açık
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Bölüm giriş animasyonu sarmalayıcı -------- */
export function Reveal({ children, delay = 0, className = '' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
